import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Sparkles, Compass, AlertCircle } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PillarSeo from "@/components/seo/PillarSeo";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import { getLearnTopic, LEARN_CATEGORIES } from "@/data/learnTopics";
import { getRelatedLearnTopics, getRelatedBlogArticlesForLearn } from "@/lib/relatedContent";

/**
 * Programmatic long-tail SEO page.
 * Each entry in `learnTopics.ts` renders here at /learn/:slug with FAQPage +
 * Article JSON-LD. Content is educational, not medical advice.
 */
const LearnTopic = () => {
  const { slug = "" } = useParams();
  const topic = getLearnTopic(slug);

  useEffect(() => {
    if (!topic) return;
    const SITE = "https://myelinahealth.com";
    const url = `${SITE}/learn/${topic.slug}`;
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE}/learn` },
        { "@type": "ListItem", position: 3, name: LEARN_CATEGORIES[topic.category], item: `${SITE}/learn` },
        { "@type": "ListItem", position: 4, name: topic.title, item: url },
      ],
    };
    const id = "learn-topic-breadcrumb-jsonld";
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.textContent = JSON.stringify(breadcrumbLd);
    document.head.appendChild(s);
    return () => { document.getElementById(id)?.remove(); };
  }, [topic]);

  if (!topic) return <Navigate to="/learn" replace />;

  const related = getRelatedLearnTopics(slug, 3);
  const relatedBlog = getRelatedBlogArticlesForLearn(slug, 2);

  return (
    <>
      <PillarSeo
        slug={`learn/${topic.slug}`}
        title={topic.title}
        description={topic.description}
        faqs={topic.faqs}
        image={`/og/learn/${topic.slug}.png`}
        imageAlt={topic.title}
      />
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-6 py-24 max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Learn", href: "/learn" },
              { name: LEARN_CATEGORIES[topic.category], href: "/learn" },
              { name: topic.title },
            ]}
          />
          <div className="mb-4">
            <Link to="/learn" className="eyebrow inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
              <Compass className="h-3 w-3" /> Learn library. {LEARN_CATEGORIES[topic.category]}
            </Link>
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">{topic.title}</h1>
          <p className="text-[17px] text-muted-foreground leading-relaxed mb-10">{topic.intro}</p>

          <section className="mb-10">
            <h2 className="font-heading text-xl font-semibold mb-4">Key points</h2>
            <ul className="space-y-2">
              {topic.keyPoints.map((k, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                  <span className="text-primary flex-shrink-0 mt-1">•</span>
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </section>

          <Card className="p-5 mb-10 bg-primary/5 border-primary/20 rounded-2xl">
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="text-[12px] uppercase tracking-wider font-medium text-primary mb-1">What the data shows</p>
                <p className="text-[15px] leading-relaxed">{topic.correlation}</p>
              </div>
            </div>
          </Card>

          <section className="mb-10">
            <h2 className="font-heading text-xl font-semibold mb-4">What to try</h2>
            <ol className="space-y-3">
              {topic.actionable.map((a, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                  <span className="font-mono text-primary text-[13px] flex-shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <span>{a}</span>
                </li>
              ))}
            </ol>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl font-semibold mb-4">Frequently asked</h2>
            <div className="space-y-4">
              {topic.faqs.map((f, i) => (
                <div key={i} className="border-b border-border/60 pb-4">
                  <p className="font-medium text-[15px] mb-2">{f.question}</p>
                  <p className="text-[14px] text-muted-foreground leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <Card className="p-6 mb-10 rounded-2xl">
            <p className="font-heading text-lg font-semibold mb-2">See your own pattern.</p>
            <p className="text-[14px] text-muted-foreground mb-4 leading-relaxed">
              A 60-second daily check-in surfaces the correlations that matter for you, not the average user.
            </p>
            <Button asChild className="rounded-xl gap-2">
              <Link to="/signup">
                Try Myelina Health free <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </Card>

          {related.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl font-semibold mb-4">Related</h2>
              <div className="grid gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/learn/${r.slug}`}
                    className="block border border-border/60 rounded-xl p-4 hover:border-primary/40 transition-colors"
                  >
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                      {LEARN_CATEGORIES[r.category]}
                    </p>
                    <p className="text-[15px] font-medium">{r.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {relatedBlog.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl font-semibold mb-4">From the blog</h2>
              <div className="grid gap-3">
                {relatedBlog.map((b) => (
                  <Link
                    key={b.slug}
                    to={`/blog/${b.slug}`}
                    className="block border border-border/60 rounded-xl p-4 hover:border-primary/40 transition-colors"
                  >
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                      Blog · {b.category}
                    </p>
                    <p className="text-[15px] font-medium">{b.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="flex gap-2 items-start text-[11px] text-muted-foreground leading-relaxed border-t border-border/60 pt-6">
            <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
            <p>
              Educational content, not medical advice. Always discuss changes to your treatment or routine with your neurology team.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default LearnTopic;
